import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNotifications } from '@/hooks/useNotifications';

export type SecurityEventType = 
  | 'login_attempt'
  | 'login_failed'
  | 'login_success'
  | 'password_change'
  | 'pin_verification_failed'
  | 'invitation_accessed'
  | 'unauthorized_access_attempt'
  | 'suspicious_activity'
  | 'input_validation_failed'
  | 'company_created'
  | 'company_creation_failed'
  | 'data_access'
  | 'data_access_failed'
  | 'invitation_cancelled'
  | 'financial_data_access'
  | 'payment_operation_audit'
  | 'rate_limit_exceeded'
  | 'rate_limit_check_failed'
  | 'unauthorized_profile_access'
  | 'profile_data_access'
  | 'invitation_data_access';

export const useSecurityMonitoring = () => {
  const { error } = useNotifications();

  const logSecurityEvent = useCallback(async (
    eventType: SecurityEventType,
    eventData?: Record<string, any>,
    showUserNotification = false
  ) => {
    try {
      const { error: logError } = await supabase.rpc('log_security_event', {
        p_event_type: eventType,
        p_event_data: eventData || {}
      });

      if (logError) {
        console.error('Failed to log security event:', logError);
      }

      // Show notification for critical events
      if (showUserNotification && (
        eventType === 'login_failed' || 
        eventType === 'unauthorized_access_attempt' ||
        eventType === 'suspicious_activity'
      )) {
        error({
          title: 'Security Alert',
          description: 'Suspicious activity detected on your account'
        });
      }
    } catch (err) {
      console.error('Security monitoring error:', err);
    }
  }, [error]);

  const trackFailedLogin = useCallback(async (email: string, reason: string) => {
    await logSecurityEvent('login_failed', {
      email: email.toLowerCase(),
      reason,
      timestamp: new Date().toISOString()
    }, true);
  }, [logSecurityEvent]);

  const trackSuccessfulLogin = useCallback(async (userId: string) => {
    await logSecurityEvent('login_success', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [logSecurityEvent]);

  const trackPinFailure = useCallback(async (attempts: number) => {
    await logSecurityEvent('pin_verification_failed', {
      attempts,
      timestamp: new Date().toISOString()
    }, attempts >= 3);
  }, [logSecurityEvent]);

  const trackUnauthorizedAccess = useCallback(async (resource: string, attemptedAction: string) => {
    await logSecurityEvent('unauthorized_access_attempt', {
      resource,
      attempted_action: attemptedAction,
      timestamp: new Date().toISOString()
    }, true);
  }, [logSecurityEvent]);

  return {
    logSecurityEvent,
    trackFailedLogin,
    trackSuccessfulLogin,
    trackPinFailure,
    trackUnauthorizedAccess,
  };
};